// Blender-authored brass-bone-and-leather fauna, with the original species
// silhouettes and authoritative posture vocabulary. Geometry and compressed
// textures stay shared across interest-managed entries; each body owns its
// material variants. Named hip pivots swing at replicated ground speed, so
// interpolated remotes need no extra animation state on the wire. The worn
// survey machine's lamp goes dark when it seizes on its feet.

import { asCreaturePosture, type CreaturePosture, type CreatureSpecies } from "@bebop/shared";
import * as THREE from "three";
import type { World } from "../core/world";
import { cloneAuthoredAsset, disposeAuthoredObject, getAuthoredTemplate } from "./authored-assets";
import { castsShadows, Tracker } from "./common";

interface CreatureEntry {
  root: THREE.Group;
  /** Body group that stance offsets (crouch, pitch, fall) apply to. */
  body: THREE.Group;
  legs: THREE.Object3D[];
  /** Cached geometry/textures remain owned by the authored-asset loader. */
  authored?: THREE.Group;
  species: CreatureSpecies;
  phase: number;
  /** Eased stance blend so posture changes read as movement, not snaps. */
  crouch: number;
  fall: number;
  lunge: number;
  /** Warden only: the lamp dies with the machine. */
  lamp?: THREE.MeshStandardMaterial;
}

interface SpeciesParts {
  build(entry: { legs: THREE.Group[] }, lamp?: THREE.MeshStandardMaterial): THREE.Group;
  /** Standing height of the body group over the ground. */
  bodyY: number;
  /** Stride frequency multiplier. */
  stride: number;
  legSwing: number;
}

function box(
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  sx: number,
  sy: number,
  sz: number,
  x: number,
  y: number,
  z: number,
): THREE.Mesh {
  const m = new THREE.Mesh(geo, mat);
  m.scale.set(sx, sy, sz);
  m.position.set(x, y, z);
  return m;
}

/** A leg pivoted at the hip so it can swing. */
function leg(
  geo: THREE.BufferGeometry,
  mat: THREE.Material,
  w: number,
  len: number,
  x: number,
  y: number,
  z: number,
): THREE.Group {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.add(box(geo, mat, w, len, w, 0, -len / 2, 0));
  return g;
}

export class CreatureLayer {
  readonly group = new THREE.Group();
  private tracker = new Tracker();
  private entries = new Map<string, CreatureEntry>();
  private unit: THREE.BufferGeometry;
  private sphere: THREE.BufferGeometry;
  private leather: THREE.Material;
  private brass: THREE.Material;
  private bone: THREE.Material;
  private darkHide: THREE.Material;
  private mire: THREE.Material;
  private verdigris: THREE.Material;
  private wornBrass: THREE.Material;
  private parts: Record<CreatureSpecies, SpeciesParts>;

  groundY: (x: number, z: number) => number = () => 0;

  constructor() {
    const t = this.tracker;
    this.unit = t.geo(new THREE.BoxGeometry(1, 1, 1));
    this.sphere = t.geo(new THREE.SphereGeometry(0.5, 8, 6));
    this.leather = t.mat(new THREE.MeshStandardMaterial({ color: 0x6b4a2e, roughness: 0.85 }));
    this.brass = t.mat(
      new THREE.MeshStandardMaterial({ color: 0x9a7a42, metalness: 0.6, roughness: 0.45 }),
    );
    this.bone = t.mat(new THREE.MeshStandardMaterial({ color: 0xc9bfa8, roughness: 0.9 }));
    this.darkHide = t.mat(new THREE.MeshStandardMaterial({ color: 0x2e2a30, roughness: 0.9 }));
    this.mire = t.mat(new THREE.MeshStandardMaterial({ color: 0x3d4a34, roughness: 0.95 }));
    this.verdigris = t.mat(
      new THREE.MeshStandardMaterial({ color: 0x4f6b5c, metalness: 0.35, roughness: 0.7 }),
    );
    this.wornBrass = t.mat(
      new THREE.MeshStandardMaterial({ color: 0x77603a, metalness: 0.55, roughness: 0.6 }),
    );

    const unit = this.unit;
    const sphere = this.sphere;

    this.parts = {
      // Heavy grazer: a barrel on four posts with a brass crest — the
      // biggest, roundest silhouette on the plains.
      brasshorn: {
        bodyY: 1.05,
        stride: 2.2,
        legSwing: 0.4,
        build: (e) => {
          const g = new THREE.Group();
          g.add(box(unit, this.leather, 1.7, 1.05, 2.5, 0, 0.45, 0));
          g.add(box(unit, this.leather, 1.2, 0.7, 0.9, 0, 0.25, 1.55)); // neck/shoulder
          g.add(box(unit, this.brass, 1.35, 0.16, 1.1, 0, 0.95, 0.6)); // back plate
          g.add(box(unit, this.brass, 0.95, 0.5, 0.65, 0, 0.45, 2.05)); // crest/head
          g.add(box(unit, this.darkHide, 0.2, 0.5, 0.2, 0, 0.5, -1.4)); // tail stub
          for (const [sx, sz] of [
            [-0.6, 0.95],
            [0.6, 0.95],
            [-0.6, -0.95],
            [0.6, -0.95],
          ] as const) {
            const l = leg(unit, this.darkHide, 0.3, 1.05, sx, 0, sz);
            e.legs.push(l);
            g.add(l);
          }
          return g;
        },
      },
      // Pack predator: long, low and pointed — all nose and tail.
      skirl: {
        bodyY: 0.62,
        stride: 3.4,
        legSwing: 0.7,
        build: (e) => {
          const g = new THREE.Group();
          g.add(box(unit, this.bone, 0.5, 0.45, 1.35, 0, 0.1, 0));
          g.add(box(unit, this.darkHide, 0.34, 0.14, 1.3, 0, 0.36, 0)); // dorsal stripe
          g.add(box(unit, this.bone, 0.3, 0.28, 0.7, 0, 0.16, 0.95)); // head
          g.add(box(unit, this.darkHide, 0.22, 0.12, 0.4, 0, 0.0, 1.2)); // jaw
          g.add(box(unit, this.bone, 0.16, 0.14, 0.9, 0, 0.2, -1.05)); // tail
          g.add(box(unit, this.darkHide, 0.1, 0.1, 0.55, 0, 0.24, -1.7)); // tail tip
          for (const [sx, sz] of [
            [-0.24, 0.5],
            [0.24, 0.5],
            [-0.24, -0.5],
            [0.24, -0.5],
          ] as const) {
            const l = leg(unit, this.bone, 0.12, 0.62, sx, 0, sz);
            e.legs.push(l);
            g.add(l);
          }
          return g;
        },
      },
      // Fen lurker: wide, flat, jaw-first — a lid on the water.
      sumpjack: {
        bodyY: 0.34,
        stride: 2.6,
        legSwing: 0.5,
        build: (e) => {
          const g = new THREE.Group();
          g.add(box(unit, this.mire, 1.35, 0.42, 1.7, 0, 0.12, 0));
          g.add(box(unit, this.mire, 0.95, 0.34, 0.85, 0, 0.08, 1.2)); // head
          g.add(box(unit, this.darkHide, 0.8, 0.16, 0.6, 0, -0.1, 1.45)); // underjaw
          g.add(box(unit, this.verdigris, 0.5, 0.18, 1.1, 0, 0.42, -0.1)); // back ridge
          g.add(box(unit, this.mire, 0.4, 0.16, 0.9, 0, 0.1, -1.2)); // tail
          for (const [sx, sz] of [
            [-0.75, 0.6],
            [0.75, 0.6],
            [-0.75, -0.6],
            [0.75, -0.6],
          ] as const) {
            const l = leg(unit, this.darkHide, 0.2, 0.36, sx, 0, sz);
            e.legs.push(l);
            g.add(l);
          }
          return g;
        },
      },
      // Rogue automaton: tall, thin, and wrong — two stilts, a hooded
      // lamp, one working arm. Nothing else on the planet stands upright
      // but the players, which is exactly why the silhouette works.
      warden: {
        bodyY: 1.7,
        stride: 1.6,
        legSwing: 0.5,
        build: (e, lamp) => {
          const g = new THREE.Group();
          g.add(box(unit, this.wornBrass, 0.6, 0.9, 0.45, 0, 0.45, 0)); // torso column
          g.add(box(unit, this.verdigris, 0.95, 0.2, 0.5, 0, 0.95, 0)); // shoulder yoke
          g.add(box(unit, this.wornBrass, 0.45, 0.35, 0.45, 0, 1.25, 0.05)); // hood
          if (lamp) {
            const eye = new THREE.Mesh(sphere, lamp);
            eye.scale.set(0.24, 0.24, 0.24);
            eye.position.set(0, 1.25, 0.28);
            g.add(eye);
          }
          const arm = box(unit, this.verdigris, 0.14, 0.85, 0.14, 0.55, 0.55, 0.1);
          arm.rotation.z = 0.25;
          g.add(arm);
          g.add(box(unit, this.wornBrass, 0.2, 0.2, 0.55, -0.5, 0.85, 0)); // vane stub
          for (const sx of [-0.22, 0.22] as const) {
            const l = leg(unit, this.darkHide, 0.14, 1.7, sx, 0, 0);
            e.legs.push(l);
            g.add(l);
          }
          return g;
        },
      },
    };
  }

  sync(world: World, dt: number): void {
    for (const [id, entry] of this.entries) {
      if (!world.creatures.has(id)) {
        this.group.remove(entry.root);
        if (entry.authored) disposeAuthoredObject(entry.authored);
        else entry.lamp?.dispose();
        this.entries.delete(id);
      }
    }

    for (const [id, creature] of world.creatures) {
      let entry = this.entries.get(id);
      if (!entry) {
        entry = this.createEntry(creature.species);
        this.entries.set(id, entry);
        castsShadows(entry.root);
        this.group.add(entry.root);
      }
      const pose = creature.renderPose;
      const posture = asCreaturePosture(creature.posture);
      this.animate(entry, posture, creature.speed, dt);
      const spec = this.parts[entry.species];
      entry.root.position.set(pose.x, this.groundY(pose.x, pose.z), pose.z);
      entry.root.rotation.y = pose.yaw;
      // Flesh bodies roll down; a seized survey machine stays on its feet.
      entry.body.position.y =
        spec.bodyY * (1 - entry.crouch * 0.25) -
        (entry.species === "warden" ? 0 : entry.fall * spec.bodyY * 0.72);
      entry.body.rotation.x = -entry.lunge * 0.4;
      entry.body.rotation.z = entry.species === "warden" ? 0 : entry.fall * 1.3;
    }
  }

  private animate(entry: CreatureEntry, posture: CreaturePosture, speed: number, dt: number): void {
    const spec = this.parts[entry.species];
    const ease = Math.min(1, dt * 6);
    entry.crouch += ((posture === "stalk" ? 1 : 0) - entry.crouch) * ease;
    entry.lunge += ((posture === "lunge" ? 1 : 0) - entry.lunge) * Math.min(1, dt * 10);
    entry.fall += ((posture === "down" ? 1 : 0) - entry.fall) * Math.min(1, dt * 4);
    if (entry.lamp) {
      const lit = posture === "down" ? 0 : posture === "idle" || posture === "wander" ? 0.7 : 1.6;
      entry.lamp.emissiveIntensity += (lit - entry.lamp.emissiveIntensity) * ease;
      entry.lamp.color.setHex(posture === "down" ? 0x29261e : 0xe8b23a);
    }

    entry.phase += speed * dt * spec.stride;
    const swing = spec.legSwing * Math.min(1, speed / 1.2) * (1 - entry.fall);
    for (let i = 0; i < entry.legs.length; i++) {
      const l = entry.legs[i];
      if (l) l.rotation.x = Math.sin(entry.phase + (i % 2 === 0 ? 0 : Math.PI)) * swing;
    }
  }

  private createEntry(species: CreatureSpecies): CreatureEntry {
    const legs: CreatureEntry["legs"] = [];
    const spec = this.parts[species];
    const assetId = species === "warden" ? "ground-warden" : species;
    if (getAuthoredTemplate(assetId)) {
      const authored = cloneAuthoredAsset(assetId);
      const body = new THREE.Group();
      // Models export at ground centre. Retain the existing hip-height stance
      // pivot, so stalk, lunge and fall still react to authoritative posture.
      authored.position.y = -spec.bodyY;
      body.add(authored);
      body.position.y = spec.bodyY;
      for (const name of ["Leg_FL", "Leg_FR", "Leg_RL", "Leg_RR"]) {
        authored.traverse((node) => {
          // Three sanitizes Blender's .001 suffix to 001. Each limb has a
          // unique prefix, so both raw and sanitized pivot names resolve.
          if (node.name.startsWith(name)) {
            // glTF multi-material nodes have mesh children; rotate their pivot once.
            if (!node.parent?.name.startsWith(name)) legs.push(node);
          }
        });
      }
      let lamp: THREE.MeshStandardMaterial | undefined;
      if (species === "warden") {
        authored.traverse((node) => {
          if (!(node instanceof THREE.Mesh)) return;
          for (const material of Array.isArray(node.material) ? node.material : [node.material]) {
            if (material instanceof THREE.MeshStandardMaterial && material.name === "fauna-eye") {
              lamp = material;
            }
          }
        });
      }
      const root = new THREE.Group();
      root.add(body);
      const entry: CreatureEntry = {
        root,
        body,
        legs,
        authored,
        species,
        phase: Math.random() * Math.PI * 2,
        crouch: 0,
        fall: 0,
        lunge: 0,
      };
      if (lamp) entry.lamp = lamp;
      return entry;
    }
    // Existing geometry remains available to isolated renderer harnesses.
    const fallbackLegs: THREE.Group[] = [];
    let lamp: THREE.MeshStandardMaterial | undefined;
    if (species === "warden") {
      lamp = new THREE.MeshStandardMaterial({
        color: 0xe8b23a,
        emissive: 0xe8b23a,
        emissiveIntensity: 0.7,
      });
    }
    const body = spec.build({ legs: fallbackLegs }, lamp);
    body.position.y = spec.bodyY;
    const root = new THREE.Group();
    root.add(body);
    const entry: CreatureEntry = {
      root,
      body,
      legs: fallbackLegs,
      species,
      phase: Math.random() * Math.PI * 2,
      crouch: 0,
      fall: 0,
      lunge: 0,
    };
    if (lamp) entry.lamp = lamp;
    return entry;
  }

  dispose(): void {
    for (const entry of this.entries.values()) {
      this.group.remove(entry.root);
      if (entry.authored) disposeAuthoredObject(entry.authored);
      else entry.lamp?.dispose();
    }
    this.entries.clear();
    this.tracker.dispose();
  }
}
